import { Html5Qrcode } from 'html5-qrcode'
import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { TokenInterface } from '@store/actionSlices/token'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import useFirebaseQRAuthService from '@firebaseUtil/firebase-qr-auth-service'

import { notifyError } from '@adUtilities/notifier'

import Error from './error'

interface ComponentProps {
  token: TokenInterface
  projectIdentity: ProjectIdentity
  room?: string
}

const SCANNING = 2
const QR_AUTH_CODE_LENGTH = 6
const ignoredErrorSnippets = [
  'NotFoundException',
  'No barcode or QR code detected',
  'No MultiFormat Readers were able to detect the code',
]

function QRAuth({ token, projectIdentity, room }: ComponentProps) {
  const dispatch = useDispatch()

  const [qrAuthCode, setQrAuthCode] = useState('')
  const [connectionError, setConnectionErrorState] = useState(false)

  const {
    qrAuthData,
    getDocument,
    updateDocument,
    doesSessionExist,
    updateConnection,
    deleteDocument,
  } = useFirebaseQRAuthService(qrAuthCode)

  const processShowcaseLogin = React.useCallback(
    async (code: string): Promise<boolean> => {
      setConnectionErrorState(false)
      const doc = await getDocument(code)

      if (!doc) {
        console.error('Document reference does not exists')
        setConnectionErrorState(true)
        return false
      }

      const {
        projectUUID: projectId,
        projectLabel: name,
        projectName: canonical_name,
      } = projectIdentity

      const updateResponse = await updateDocument(qrAuthCode, {
        authData: JSON.stringify({
          project: {
            id: projectId,
            name,
            canonical_name,
          },
          user: token,
          showcaseRoom: room,
        }),
      })

      if (!updateResponse) {
        console.error('Failed to update the document')
        setConnectionErrorState(true)
        return false
      }

      return true
    },
    [projectIdentity, qrAuthCode]
  )

  const connectSession = React.useCallback(
    async (sessionId: string): Promise<boolean> => {
      const { projectId, projectName, projectUUID } = projectIdentity

      const sessionExist = await doesSessionExist({
        projectName: projectId,
        sessionId,
      })

      if (!sessionExist) {
        console.error('Session does not exist')
        setConnectionErrorState(true)
        return false
      }

      await updateConnection({
        connected: true,
        projectId,
        sessionId,
      })

      dispatch(
        setProjectIdentity({
          ...projectIdentity,
          sessionId,
          sessionList: [
            {
              sessionName: sessionId,
              sessionId,
              projectId,
              projectName,
              projectUUID,
            },
            ...projectIdentity.sessionList,
          ],
        })
      )

      await deleteDocument(qrAuthCode)

      return true
    },
    [projectIdentity, qrAuthCode]
  )

  const getCodeFromQRText = (decodedText: string): string => {
    const qrDataObject = JSON.parse(decodedText)
    const { issuer = '', code = '' } = qrDataObject
    if (issuer !== 'showcase' || code.length !== QR_AUTH_CODE_LENGTH) {
      return ''
    }
    return code
  }

  const handleReScanClick = () => {
    setQrAuthCode('')
    setConnectionErrorState(false)
  }

  useEffect(() => {
    if (!qrAuthCode) {
      const html5QrCode = new Html5Qrcode('qr-reader')

      html5QrCode
        .start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 250 },
          (decodedText) => {
            const code = getCodeFromQRText(decodedText)
            if (!code) {
              notifyError(
                'Invalid QR, please recreate the QR in the showcase app'
              )
              return
            }
            html5QrCode.stop().then(() => {
              html5QrCode.clear()
            })
            setQrAuthCode(code)
          },
          (error) => {
            if (
              !ignoredErrorSnippets.some((snippet) => error.includes(snippet))
            ) {
              notifyError(
                'Something went wrong, please refresh the app and try again'
              )
              console.error(`QR Code scan error: ${error}`)
            }
          }
        )
        .catch((error) => {
          notifyError(
            'Something went wrong, please refresh the app and try again'
          )
          console.error('Failed to start scanner', error)
        })

      return () => {
        if (html5QrCode && html5QrCode.getState() === SCANNING) {
          html5QrCode.stop().then(() => {
            html5QrCode.clear()
          })
        }
      }
    }

    return undefined
  }, [qrAuthCode])

  useEffect(() => {
    if (qrAuthCode) {
      processShowcaseLogin(qrAuthCode)
    }
  }, [qrAuthCode])

  useEffect(() => {
    if (qrAuthData && qrAuthData.sessionId) {
      connectSession(qrAuthData.sessionId)
    }
  }, [qrAuthData?.sessionId])

  return (
    <>
      {qrAuthCode ? (
        <div className="flex min-h-[450px] w-full items-center justify-center bg-white">
          {connectionError ? (
            <Error onClick={handleReScanClick} />
          ) : (
            'Connecting...'
          )}
        </div>
      ) : (
        <div id="qr-reader" className="h-full w-full"></div>
      )}
    </>
  )
}

export default connect(
  ({ token, projectIdentity, appConfig: { room } }: RootStateTypeExtra) => ({
    token,
    projectIdentity,
    room,
  })
)(QRAuth)
